import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useCreateFAQ, useUpdateFAQ, useFAQCategories } from '@/hooks/use-faq'
import type { TenantFaq } from '@/hooks/use-faq'

interface FormState {
  question: string
  answer: string
  category: string
  is_published: boolean
}

const EMPTY_FORM: FormState = {
  question: '',
  answer: '',
  category: '',
  is_published: true,
}

interface FAQFormModalProps {
  open: boolean
  onClose: () => void
  faq?: TenantFaq
}

export default function FAQFormModal({ open, onClose, faq }: FAQFormModalProps) {
  const isEdit = !!faq
  const createFAQ = useCreateFAQ()
  const updateFAQ = useUpdateFAQ()
  const existingCategories = useFAQCategories()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  useEffect(() => {
    if (open) {
      if (faq) {
        setForm({
          question: faq.question,
          answer: faq.answer,
          category: faq.category ?? '',
          is_published: faq.is_published,
        })
      } else {
        setForm(EMPTY_FORM)
      }
      createFAQ.reset()
      updateFAQ.reset()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, faq?.id])

  const isPending = createFAQ.isPending || updateFAQ.isPending
  const mutationError = createFAQ.error ?? updateFAQ.error

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = {
      question: form.question.trim(),
      answer: form.answer.trim(),
      category: form.category.trim() || null,
      is_published: form.is_published,
    }

    if (isEdit && faq) {
      updateFAQ.mutate({ id: faq.id, ...payload }, { onSuccess: onClose })
    } else {
      createFAQ.mutate(payload, { onSuccess: onClose })
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative z-10 w-full max-w-lg rounded-xl border border-border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-semibold">{isEdit ? 'Edit FAQ' : 'Tambah FAQ'}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Question */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Pertanyaan <span className="text-destructive">*</span>
            </label>
            <textarea
              required
              rows={2}
              value={form.question}
              onChange={(e) => set('question', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-none"
              placeholder="Misalnya: Bagaimana cara memesan cheki?"
            />
          </div>

          {/* Answer */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Jawaban <span className="text-destructive">*</span>
            </label>
            <textarea
              required
              rows={4}
              value={form.answer}
              onChange={(e) => set('answer', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none resize-y"
              placeholder="Tulis jawaban lengkap di sini..."
            />
          </div>

          {/* Category */}
          <div className="space-y-1">
            <label className="text-sm font-medium">Kategori</label>
            <input
              list="faq-categories"
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
              placeholder="Misalnya: Pemesanan, Pembayaran (opsional)"
            />
            {existingCategories.length > 0 && (
              <datalist id="faq-categories">
                {existingCategories.map((cat) => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
            )}
            <p className="text-xs text-muted-foreground">
              FAQ dengan kategori yang sama akan dikelompokkan di halaman publik.
            </p>
          </div>

          {/* Published */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => set('is_published', e.target.checked)}
              className="rounded border-border accent-primary"
            />
            <span className="text-sm">Tampilkan di halaman publik</span>
          </label>

          {/* Error */}
          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan. Coba lagi.'}
            </p>
          )}

          {/* Footer actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah FAQ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
